import { CommonModule, CurrencyPipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  InjectionToken,
  NgModule,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import {
  initSDK,
  PlacesAutocompleteDirective,
} from '@credit-acceptance/www-api-sdk-fe';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { AdobeECIDInterceptor } from '../libs/adobe-analytics/src/lib/libs/adobe-ecid.interceptor';
import { AccountsComponent } from './accounts/accounts.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ErrorPageComponent } from './error-page/error-page.component';
import {
  FeatureFlagService,
  initializeFeatureFlagService,
} from './feature-flag.service';
import { FinalSuccessResponseComponent } from './final-success-response/final-success-response.component';
import { IdentificationComponent } from './identification/identification.component';
import { IncomeComponent } from './income/income.component';
import { MainContainerComponent } from './main-container/main-container.component';
import { OpenLoanComponent } from './openloan/openloan.component';
import { PersonalComponent } from './personal/personal.component';
import { ProductTermsComponent } from './product-terms/product-terms.component';
import { ProgressBarComponent } from './progress-bar/progress-bar.component';
import { ResidenceComponent } from './residence/residence.component';
import { RuntimeConfigService } from './services/runtime-config';
import { AppPasswordDirective } from './shared/directives/app-password.directive';
import { TwoDigitDecimaNumberDirective } from './shared/directives/app-two-digit-decimal-number.directive';
import { OnlyAlphabetsDirective } from './shared/directives/only-alphabets.directive';
import { StreetAddressDirective } from './shared/directives/street-address.directive';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
import { PhoneFormatPipe } from './shared/pipes/phone-format.pipe';
import { SafeHtmlPipe } from './shared/pipes/safe-html.pipe';
import { SmsConsentComponent } from './sms-consent/sms-consent.component';
import { SuccessPrequalComponent } from './success-prequal/success-prequal.component';
import { SummaryComponent } from './summary/summary.component';
import { VehicleTypeComponent } from './vehicle-type/vehicle-type.component';
import { VerificationRequiredComponent } from './verification-required/verification-required.component';

// TODO: Remove
export const APP_CONFIG = new InjectionToken<any>('app.config');

/**
 * Bootstraps the www-api-sdk-fe Google Maps client (places library) before any
 * component renders, so the PlacesAutocompleteDirective has an initialized Maps
 * client. Mirrors the FeatureFlagService APP_INITIALIZER pattern and reads the
 * key from runtime config. Fails soft: a missing key leaves autocomplete
 * inactive rather than blocking app start.
 */
export function initializePlacesSdk(
  runtimeConfigService: RuntimeConfigService
): () => Promise<void> {
  return () =>
    initSDK({
      googleMaps: {
        apiKey: runtimeConfigService.prequalConfig['GOOGLE_MAPS_API_KEY'] || '',
      },
      // Never let a failed Maps init block app bootstrap: the autocomplete
      // widget fails soft (no suggestions) and manual address entry still works.
    }).catch(() => undefined);
}

@NgModule({
  declarations: [
    AppComponent,
    PersonalComponent,
    OnlyAlphabetsDirective,
    StreetAddressDirective,
    ResidenceComponent,
    IdentificationComponent,
    ProgressBarComponent,
    IncomeComponent,
    MainContainerComponent,
    AccountsComponent,
    TwoDigitDecimaNumberDirective,
    AppPasswordDirective,
    IdentificationComponent,
    ErrorPageComponent,
    SummaryComponent,
    FinalSuccessResponseComponent,
    SuccessPrequalComponent,
    OpenLoanComponent,
    VerificationRequiredComponent,
    SafeHtmlPipe,
    PhoneFormatPipe,
    ProductTermsComponent,
    SmsConsentComponent,
    VehicleTypeComponent,
  ],
  bootstrap: [AppComponent],
  exports: [SafeHtmlPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule.forRoot([]),
    AppRoutingModule,
    BrowserAnimationsModule,
    NgxMaskDirective,
    PlacesAutocompleteDirective,
    NgbModule,
  ],
  providers: [
    CurrencyPipe,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AdobeECIDInterceptor, multi: true },
    PhoneFormatPipe,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFeatureFlagService,
      deps: [FeatureFlagService],
      multi: true,
    },
    // TODO: Remove and see if other resources tied to this can be removed as well // { provide: APP_CONFIG, useValue: appConfig },
    provideNgxMask(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializePlacesSdk,
      deps: [RuntimeConfigService],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
